import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { AppointmentStatus, Store } from '@geely/shared';
import { BOOKING_START_DATE } from '@geely/shared';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string): string {
  if (!date) return 'Data inválida';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

export function formatTime(time: string): string {
  return time;
}

export function getDayOfWeekLabel(day: string): string {
  if (!day) return '-';
  const dateObj = new Date(day + 'T00:00:00');
  const dayOfWeek = dateObj.toLocaleDateString('pt-BR', { weekday: 'long' });
  return dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
}

export function getStatusLabel(status: AppointmentStatus): string {
  const labels: Record<AppointmentStatus, string> = {
    pending: 'Pendente',
    in_progress: 'Em andamento',
    completed: 'Concluído',
    cancelled: 'Cancelado',
    customer_absent: 'Cliente ausente',
  };
  return labels[status];
}

export function getStatusColor(status: AppointmentStatus): string {
  const colors: Record<AppointmentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
    completed: 'bg-green-100 text-green-800 border-green-300',
    cancelled: 'bg-red-100 text-red-800 border-red-300',
    customer_absent: 'bg-gray-100 text-gray-800 border-gray-300',
  };
  return colors[status];
}

// Get available dates for booking (next daysAhead days from today or BOOKING_START_DATE, whichever is later)
export function getAvailableDates(daysAhead: number = 60): string[] {
  const dates: string[] = [];
  const bookingStart = new Date(BOOKING_START_DATE + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = bookingStart > today ? bookingStart : today;
  const startIndex = bookingStart > today ? 0 : 1;

  for (let i = startIndex; i < startIndex + daysAhead; i++) {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }

  return dates;
}

// Format date for display in select options
export function formatDateForSelect(date: string): string {
  if (!date) return 'Data inválida';
  const [year, month, day] = date.split('-');
  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.toLocaleDateString('pt-BR', { weekday: 'long' });
  const capitalizedDayOfWeek = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
  return `${capitalizedDayOfWeek}, ${day}/${month}/${year}`;
}

// Get unique states from stores
export function getAvailableStates(stores: Store[]): string[] {
  const states = stores.map(s => s.state);
  return Array.from(new Set(states)).sort();
}

// Get cities filtered by state
export function getCitiesByState(stores: Store[], state: string): string[] {
  const cities = stores
    .filter(s => s.state === state)
    .map(s => s.city);
  return Array.from(new Set(cities)).sort();
}

// Get stores filtered by state and city
export function getStoresByStateAndCity(
  stores: Store[],
  state: string,
  city: string
): Store[] {
  return stores.filter(s => s.state === state && s.city === city);
}

// Get full state name from state code
export function getStateName(stateCode: string): string {
  const stateNames: Record<string, string> = {
    'SP': 'São Paulo',
    'RJ': 'Rio de Janeiro',
    'DF': 'Distrito Federal',
    'MG': 'Minas Gerais',
    'PR': 'Paraná',
  };
  return stateNames[stateCode] || stateCode;
}
